"use client";

import { Textarea } from "@/components/ui/textarea";
import { Field, FieldError, FieldLabel, useFieldWrapperContext } from "./field-wrapper";
import type { FieldBindingProps } from "./types";

export interface TextareaFieldProps extends FieldBindingProps<string> {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Focusable but un-typeable. Use this (not `disabled`) when an external
   * coordinator gates typing while still wanting click/focus to flow through
   * — e.g. chart-lock acquire-on-focus. `disabled` would suppress focus
   * events entirely, blocking the acquire trigger.
   */
  readOnly?: boolean;
  isRequired?: boolean;
}

export function TextareaField({
  value,
  onChange,
  onBlur,
  errors,
  isTouched,
  isRequired,
  label,
  placeholder,
  disabled,
  readOnly,
}: TextareaFieldProps) {
  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <FieldTextarea
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        hasLabel={!!label}
      />
      <FieldError />
    </Field>
  );
}

function FieldTextarea({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  readOnly,
  hasLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  hasLabel: boolean;
}) {
  const { id, hasError } = useFieldWrapperContext();
  return (
    <Textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      aria-invalid={hasError}
      aria-describedby={hasError ? `${id}-error` : undefined}
      className={hasLabel ? "mt-1" : ""}
    />
  );
}
